import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MapPin, Map, MessageSquare, Check, Navigation, ChevronDown, ChevronUp, Clock } from "lucide-react";
import { B2BTaskBadge, hasBusinessStatus } from "../../components/b2b/B2BBadges";
import { EvidenceBadge } from "../../components/task/EvidenceBadge";
import { PriorityTimer } from "../../App";
import { TaskChatModal } from "../../components/task/TaskChatModal";
import { getTaskCardClasses } from "../../utils/taskStyles";
import { TaskMap } from "../../components/task/TaskMap";
import { getCurrentLocation } from "../../utils/maps";
import { useModalHistory } from "../../utils/historyNav";
import { PaginationControls } from "../../components/ui/PaginationControls";

import { CommissionPromoBannerSlim } from "../../components/commission/CommissionInfo";
import { GENERAL_SUBCATEGORIES, getSubcategoryLabel } from "../../utils/generalSubcategories";
import { TaskDescription } from "../../components/task/TaskDescription";
import { TaskSearchInput } from "../../components/task/TaskSearchInput";

// TZ10-1: удалённое задание — явно помечено или не имеет координат
const isRemoteTask = (task) => task.location_required === false || task.latitude == null || task.longitude == null;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Города для ручного выбора, если браузер не отдал геолокацию —
// без координат радиус поиска не работает вообще
const CITY_OPTIONS = [
  { value: 'krasnodar', label: 'Краснодар', latitude: 45.0355, longitude: 38.9753 },
  { value: 'sochi', label: 'Сочи', latitude: 43.5855, longitude: 39.7231 },
  { value: 'novorossiysk', label: 'Новороссийск', latitude: 44.7239, longitude: 37.7686 },
  { value: 'anapa', label: 'Анапа', latitude: 44.8949, longitude: 37.3164 },
  { value: 'armavir', label: 'Армавир', latitude: 44.9892, longitude: 41.1234 },
  { value: 'rostov', label: 'Ростов-на-Дону', latitude: 47.2357, longitude: 39.7015 },
  { value: 'stavropol', label: 'Ставрополь', latitude: 45.0428, longitude: 41.9734 },
  { value: 'moscow', label: 'Москва', latitude: 55.7558, longitude: 37.6173 },
  { value: 'spb', label: 'Санкт-Петербург', latitude: 59.9386, longitude: 30.3141 },
  { value: 'ekaterinburg', label: 'Екатеринбург', latitude: 56.8389, longitude: 60.6057 },
  { value: 'novosibirsk', label: 'Новосибирск', latitude: 55.0084, longitude: 82.9357 },
  { value: 'kazan', label: 'Казань', latitude: 55.7963, longitude: 49.1088 },
];

export const TaskList = ({ user, showToast, setCurrentView }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [showMap, setShowMap] = useState(false);
  // Загружаем сохранённый радиус из localStorage или используем 10 км по умолчанию
  const [radius, setRadius] = useState(() => {
    const saved = localStorage.getItem('reversia_search_radius');
    return saved ? parseInt(saved, 10) : 10;
  });
  const [sortBy, setSortBy] = useState('default');
  const [serviceTypeFilter, setServiceTypeFilter] = useState('all');
  const [b2bOnly, setB2bOnly] = useState(false);
  const [subcategoryFilter, setSubcategoryFilter] = useState('all');
  const [mapInstance, setMapInstance] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  // Инструкция свёрнута по умолчанию; если исполнитель развернул её вручную — состояние запоминается
  const [showInstructions, setShowInstructions] = useState(
    () => localStorage.getItem('reversia_instructions_open') === '1'
  );
  const [locationLoading, setLocationLoading] = useState(true);
  // Источник координат: 'gps' — браузер, 'city' — выбранный вручную город, null — координат нет
  const [locationSource, setLocationSource] = useState(null);
  const [manualCity, setManualCity] = useState(() => localStorage.getItem('reversia_manual_city') || '');
  // Refs для предотвращения конфликта состояний карты
  const mapRef = React.useRef(null);
  const pendingFocusRef = React.useRef(null);
  const mapSectionRef = React.useRef(null);

  // Модалка "Ознакомиться" создаёт запись истории: жест "назад" закрывает её, а не выбрасывает из приложения
  useModalHistory(!!selectedTask, () => setSelectedTask(null));

  // Серверная пагинация списка заданий
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalTasks, setTotalTasks] = useState(0);
  const [typeCounts, setTypeCounts] = useState({ all: 0, general: 0, secret_shopper: 0, grave_care: 0, mobile_journalist: 0 });
  // Отдельный (не постраничный) список для маркеров на карте — карта должна показывать
  // все подходящие задания в радиусе, а не только текущую страницу списка
  const [mapTasks, setMapTasks] = useState([]);
  // TZ10-3: поиск по ключевым словам (серверный параметр search)
  const [searchQuery, setSearchQuery] = useState('');
  const [loadedOnce, setLoadedOnce] = useState(false);
  const handleSearch = React.useCallback((q) => { setSearchQuery(q); setCurrentPage(1); }, []);

  // Сохраняем радиус в localStorage при изменении
  useEffect(() => {
    localStorage.setItem('reversia_search_radius', radius.toString());
  }, [radius]);

  // Синхронизируем mapRef с актуальным mapInstance
  useEffect(() => {
    mapRef.current = mapInstance;
  }, [mapInstance]);

  // При закрытии карты компонент TaskMap уничтожает свой ymaps-инстанс —
  // сбрасываем ссылку, чтобы фокусировка не пыталась обратиться к уже
  // разрушенной карте при следующем открытии.
  useEffect(() => {
    if (!showMap) {
      setMapInstance(null);
    }
  }, [showMap]);

  // Сначала получаем геолокацию
  useEffect(() => {
    getUserLocation();
  }, []);

  // Загружаем задания когда геолокация определена (или не удалось её получить),
  // а также при изменении страницы/радиуса/фильтров/сортировки (серверная пагинация)
  useEffect(() => {
    if (!locationLoading) {
      fetchTasks();
    }
    // eslint-disable-next-line
  }, [locationLoading, userLocation, radius, currentPage, perPage, serviceTypeFilter, subcategoryFilter, sortBy, b2bOnly, searchQuery]);

  // Карта показывает ВСЕ подходящие задания в радиусе (не только текущую страницу),
  // поэтому подгружаем отдельный, более широкий список только когда карта открыта
  useEffect(() => {
    if (!locationLoading && showMap) {
      fetchMapTasks();
    }
    // eslint-disable-next-line
  }, [locationLoading, userLocation, radius, serviceTypeFilter, subcategoryFilter, showMap, b2bOnly, searchQuery]);

  const getUserLocation = async () => {
    try {
      const location = await getCurrentLocation();
      setUserLocation(location);
      setLocationSource('gps');
    } catch (error) {
      console.error("Error getting location:", error);
      // Геолокация не получена — подставляем ранее выбранный вручную город,
      // иначе радиус поиска работать не может
      const saved = localStorage.getItem('reversia_manual_city');
      const city = CITY_OPTIONS.find(c => c.value === saved);
      if (city) {
        setUserLocation({ latitude: city.latitude, longitude: city.longitude });
        setLocationSource('city');
      } else {
        setUserLocation(null);
        setLocationSource(null);
      }
    } finally {
      setLocationLoading(false);
    }
  };

  const retryGeolocation = () => {
    setLocationLoading(true);
    getUserLocation();
  };

  const applyManualCity = (value) => {
    setManualCity(value);
    setCurrentPage(1);
    if (!value) {
      localStorage.removeItem('reversia_manual_city');
      if (locationSource === 'city') {
        setUserLocation(null);
        setLocationSource(null);
      }
      return;
    }
    const city = CITY_OPTIONS.find(c => c.value === value);
    if (!city) return;
    localStorage.setItem('reversia_manual_city', value);
    setUserLocation({ latitude: city.latitude, longitude: city.longitude });
    setLocationSource('city');
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      let url = `${API}/tasks`;
      if (userLocation) {
        url = `${API}/tasks/nearby?latitude=${userLocation.latitude}&longitude=${userLocation.longitude}&radius=${radius}`;
      }
      const params = new URLSearchParams();
      params.set('page', currentPage);
      params.set('limit', perPage);
      params.set('sort', sortBy);
      if (serviceTypeFilter !== 'all') params.set('service_type', serviceTypeFilter);
      if (serviceTypeFilter === 'general' && subcategoryFilter !== 'all') params.set('task_subcategory', subcategoryFilter);
      if (b2bOnly) params.set('b2b_only', 'true');
      if (searchQuery) params.set('search', searchQuery);
      url += (url.includes('?') ? '&' : '?') + params.toString();
      const response = await axios.get(url);
      setTasks(response.data.items || []);
      setTotalTasks(response.data.total || 0);
      setTypeCounts(response.data.type_counts || { all: 0, general: 0, secret_shopper: 0, grave_care: 0, mobile_journalist: 0 });
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  };

  const fetchMapTasks = async () => {
    try {
      let url = `${API}/tasks`;
      if (userLocation) {
        url = `${API}/tasks/nearby?latitude=${userLocation.latitude}&longitude=${userLocation.longitude}&radius=${radius}`;
      }
      const params = new URLSearchParams();
      params.set('page', 1);
      params.set('limit', 200);
      if (serviceTypeFilter !== 'all') params.set('service_type', serviceTypeFilter);
      if (serviceTypeFilter === 'general' && subcategoryFilter !== 'all') params.set('task_subcategory', subcategoryFilter);
      if (b2bOnly) params.set('b2b_only', 'true');
      if (searchQuery) params.set('search', searchQuery);
      url += (url.includes('?') ? '&' : '?') + params.toString();
      const response = await axios.get(url);
      setMapTasks(response.data.items || []);
    } catch (error) {
      console.error("Error fetching map tasks:", error);
    }
  };

  const acceptTask = async (taskId) => {
    try {
      console.log('Attempting to accept task:', taskId);
      await axios.post(`${API}/tasks/${taskId}/accept`);
      if (typeof window !== 'undefined' && window.ym) {
        window.ym(109617150, 'reachGoal', 'task_accepted');
      }
      showToast("Задание принято!", "success");
      fetchTasks();
    } catch (error) {
      console.error('Accept task error:', error.response?.data);
      const errorMessage = error.response?.data?.detail || "Ошибка при принятии задания";
      showToast(`Ошибка: ${errorMessage}`, "error");
    }
  };

  const startTaskDiscussion = async (taskId) => {
    try {
      const response = await axios.post(`${API}/tasks/${taskId}/start-discussion`);
      showToast(response.data.message, "success");
      const updatedTask = tasks.find(t => t.id === taskId);
      setTasks(tasks.map(task => 
        task.id === taskId ? { 
          ...task, 
          status: "discussing", 
          discussing_executor_id: user.id 
        } : task
      ));
      // Сразу открываем чат с заказчиком, чтобы исполнитель не искал его сам
      if (updatedTask) {
        setSelectedTask({ ...updatedTask, status: "discussing", discussing_executor_id: user.id });
      }
    } catch (error) {
      showToast(error.response?.data?.detail || "Ошибка при начале обсуждения", "error");
    }
  };

  const updateRadius = (newRadius) => {
    setCurrentPage(1);
    setRadius(newRadius);
  };

  const focusOnTaskLocation = (task) => {
    if (!task.latitude || !task.longitude) return;

    // Гарантируем, что маркер этого задания появится на карте, даже если общий
    // список mapTasks ещё не успел подгрузиться с сервера (гонка состояний).
    setMapTasks(prev => (prev.some(t => t.id === task.id) ? prev : [...prev, task]));

    const doFocus = () => {
      mapRef.current.setCenter([task.latitude, task.longitude]);
      mapRef.current.setZoom(15);
      showToast(`Карта сфокусирована на: ${task.location}`, "info");
      // Карта отрисовывается в верхней части страницы — прокручиваем к ней,
      // чтобы пользователь сразу увидел результат фокусировки.
      if (mapSectionRef.current) {
        mapSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    if (mapRef.current) {
      doFocus();
      return;
    }

    // Карта ещё не открыта/не инициализирована — открываем и ждём готовности.
    // Опрашиваем готовность вместо фиксированной задержки, т.к. загрузка
    // Yandex Maps SDK может занять разное время в зависимости от сети.
    setShowMap(true);
    if (pendingFocusRef.current) clearInterval(pendingFocusRef.current);
    let attempts = 0;
    pendingFocusRef.current = setInterval(() => {
      attempts += 1;
      if (mapRef.current) {
        clearInterval(pendingFocusRef.current);
        pendingFocusRef.current = null;
        doFocus();
      } else if (attempts > 25) {
        // ~5 секунд ожидания — если карта так и не инициализировалась, прекращаем попытки
        clearInterval(pendingFocusRef.current);
        pendingFocusRef.current = null;
      }
    }, 200);
  };

  // Функция для расчёта примерного времени в пути
  const getEstimatedTime = (distanceKm) => {
    if (!distanceKm) return null;
    // Средняя скорость в городе ~30 км/ч, за городом ~50 км/ч
    // Используем среднюю ~35 км/ч для смешанных условий
    const avgSpeedKmH = 35;
    const timeHours = distanceKm / avgSpeedKmH;
    const timeMinutes = Math.round(timeHours * 60);
    
    if (timeMinutes < 60) {
      return `~${timeMinutes} мин`;
    } else {
      const hours = Math.floor(timeMinutes / 60);
      const mins = timeMinutes % 60;
      return mins > 0 ? `~${hours} ч ${mins} мин` : `~${hours} ч`;
    }
  };

  // Функция для открытия навигатора с маршрутом
  const openNavigator = (task) => {
    if (!task.latitude || !task.longitude) {
      showToast("Координаты задания недоступны", "error");
      return;
    }
    
    // Определяем, мобильное ли устройство
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    if (isMobile) {
      // На мобильных открываем Яндекс Навигатор или Google Maps
      // Яндекс Навигатор: yandexnavi://build_route_on_map?lat_to=...&lon_to=...
      // Google Maps: https://www.google.com/maps/dir/?api=1&destination=lat,lng
      const yandexNaviUrl = `yandexnavi://build_route_on_map?lat_to=${task.latitude}&lon_to=${task.longitude}`;
      const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${task.latitude},${task.longitude}`;
      
      // Пробуем открыть Яндекс Навигатор, если не получится - Google Maps
      window.location.href = yandexNaviUrl;
      setTimeout(() => {
        window.open(googleMapsUrl, '_blank');
      }, 500);
    } else {
      // На десктопе открываем Яндекс Карты с маршрутом
      const yandexMapsUrl = userLocation 
        ? `https://yandex.ru/maps/?rtext=${userLocation.latitude},${userLocation.longitude}~${task.latitude},${task.longitude}&rtt=auto`
        : `https://yandex.ru/maps/?pt=${task.longitude},${task.latitude}&z=15`;
      window.open(yandexMapsUrl, '_blank');
    }
  };

  const SERVICE_TYPES = [
    { value: 'all',               label: 'Все типы' },
    { value: 'general',           label: 'Общие услуги' },
    { value: 'secret_shopper',    label: 'Тайный покупатель' },
    { value: 'grave_care',        label: 'Мемориальный сервис' },
    { value: 'mobile_journalist', label: 'Мобильный корреспондент' },
  ];

  const getTypeCount = (value) => typeCounts[value] || 0;

  const handleServiceTypeFilter = (value) => {
    setServiceTypeFilter(value);
    setSubcategoryFilter('all');
    setCurrentPage(1);
  };

  const handleSubcategoryFilter = (value) => {
    setSubcategoryFilter(value);
    setCurrentPage(1);
  };

  if ((loading && !loadedOnce) || locationLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3">
        <div className="w-7 h-7 rounded-full border-2 border-t-transparent animate-spin"
          style={{borderColor:'var(--rv-teal)', borderTopColor:'transparent'}} />
        <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>
          {locationLoading ? "Определение местоположения..." : "Загрузка заданий..."}
        </p>
      </div>
    );
  }

  const getServiceBadge = (task) => {
    if (!task.service_type || task.service_type === "general") return null;
    const map = {
      secret_shopper: { label: "Тайный покупатель", bg: "#EDE9FE", color: "#6D28D9" },
      grave_care:     { label: "Мемориальный сервис", bg: "#F3F4F6", color: "#374151" },
      mobile_journalist: { label: "Мобильный корреспондент", bg: "#D1FAE5", color: "#065F46" },
    };
    const s = map[task.service_type];
    if (!s) return null;
    return (
      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
        style={{background: s.bg, color: s.color}}>
        {s.label}
      </span>
    );
  };


  return (
    <div className="space-y-5">
      {/* Баннер активной акции */}
      <CommissionPromoBannerSlim />

      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <h2 className="text-2xl font-bold" style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
          Доступные задания
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <TaskSearchInput value={searchQuery} onSearch={handleSearch} />
          <div
            className="flex items-center gap-2 px-2.5 py-1 rounded-lg"
            data-testid="radius-selector-wrapper"
            style={{
              background: userLocation ? 'rgba(13,148,136,0.08)' : 'rgba(148,163,184,0.12)',
              border: userLocation ? '1px solid var(--rv-teal)' : '1px solid var(--rv-border)',
            }}
          >
            <span className="text-sm font-semibold" style={{color: userLocation ? 'var(--rv-teal)' : 'var(--rv-text-muted)'}}>Радиус поиска:</span>
            <select
              data-testid="radius-select"
              value={radius}
              disabled={!userLocation}
              onChange={(e) => updateRadius(Number(e.target.value))}
              className="px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{
                border: userLocation ? '1px solid var(--rv-teal)' : '1px solid var(--rv-border)',
                background: userLocation ? 'white' : '#F1F5F9',
                color: userLocation ? 'var(--rv-text-primary)' : 'var(--rv-text-muted)',
                fontFamily: 'IBM Plex Sans,sans-serif',
                cursor: userLocation ? 'pointer' : 'not-allowed',
              }}
              title={!userLocation ? "Радиус доступен после определения местоположения или выбора города" : undefined}
            >
              <option value={10}>10 км</option>
              <option value={25}>25 км</option>
              <option value={50}>50 км</option>
              <option value={100}>100 км</option>
              <option value={250}>250 км</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm" style={{color:'var(--rv-text-secondary)'}}>Сортировка:</span>
            <select
              data-testid="sort-select"
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentPage(1); }}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{border:'1px solid var(--rv-border)', background:'white', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif'}}
            >
              <option value="default">По расстоянию</option>
              <option value="price_desc">Цена: высокая</option>
              <option value="price_asc">Цена: низкая</option>
              <option value="date_desc">Дата: новые</option>
              <option value="date_asc">Дата: старые</option>
            </select>
          </div>
          <label className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer" data-testid="b2b-only-filter"
            style={{border: b2bOnly ? '1px solid #7DD3FC' : '1px solid var(--rv-border)', background: b2bOnly ? '#F0F9FF' : 'white', color: b2bOnly ? '#0C4A6E' : 'var(--rv-text-secondary)'}}>
            <input type="checkbox" checked={b2bOnly} onChange={(e) => { setB2bOnly(e.target.checked); setCurrentPage(1); }} style={{accentColor:'#0369A1'}} />
            Только от организаций
          </label>
          <button
            data-testid="toggle-map-btn"
            onClick={() => {
              if (pendingFocusRef.current) { clearInterval(pendingFocusRef.current); pendingFocusRef.current = null; }
              setShowMap(prev => !prev);
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{
              background: showMap ? 'var(--rv-navy)' : 'white',
              color: showMap ? 'white' : 'var(--rv-text-secondary)',
              border: '1px solid var(--rv-border)',
              cursor: 'pointer',
              fontFamily: 'IBM Plex Sans, sans-serif',
            }}
          >
            <Map size={14} />
            {showMap ? "Скрыть карту" : "На карте"}
          </button>
        </div>
      </div>

      {/* Местоположение: без координат радиус поиска не работает */}
      {!userLocation ? (
        <div
          className="rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between"
          data-testid="location-notice"
          style={{background:'#FFFBEB', border:'1px solid #FDE68A'}}
        >
          <div className="text-sm" style={{color:'#92400E'}}>
            <strong>Радиус поиска пока не работает:</strong> браузер не сообщил ваше местоположение, поэтому показаны все задания без фильтра по расстоянию. Разрешите доступ к геолокации или выберите город вручную.
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <button
              data-testid="retry-geolocation-btn"
              onClick={retryGeolocation}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
            >
              <Navigation size={14} />
              Определить меня
            </button>
            <select
              data-testid="manual-city-select"
              value={manualCity}
              onChange={(e) => applyManualCity(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{border:'1px solid #FDE68A', background:'white', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif', cursor:'pointer'}}
            >
              <option value="">Выбрать город</option>
              {CITY_OPTIONS.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
        </div>
      ) : locationSource === 'city' ? (
        <div
          className="rounded-xl px-4 py-2.5 flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between"
          data-testid="location-city-notice"
          style={{background:'rgba(13,148,136,0.07)', border:'1px solid var(--rv-border)'}}
        >
          <span className="text-sm" style={{color:'var(--rv-text-secondary)'}}>
            Задания подбираются вокруг города <strong>{CITY_OPTIONS.find(c => c.value === manualCity)?.label || '—'}</strong> в радиусе {radius} км.
          </span>
          <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
            <select
              data-testid="manual-city-select-active"
              value={manualCity}
              onChange={(e) => applyManualCity(e.target.value)}
              className="px-3 py-1.5 rounded-lg text-sm"
              style={{border:'1px solid var(--rv-border)', background:'white', color:'var(--rv-text-primary)', fontFamily:'IBM Plex Sans,sans-serif', cursor:'pointer'}}
            >
              <option value="">Сбросить город</option>
              {CITY_OPTIONS.map(c => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            <button
              data-testid="retry-geolocation-active-btn"
              onClick={retryGeolocation}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium"
              style={{background:'white', color:'var(--rv-teal)', border:'1px solid var(--rv-teal)', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
            >
              <Navigation size={14} />
              Определить меня
            </button>
          </div>
        </div>
      ) : null}

      {/* Фильтр по типу задания */}
      <div className="flex flex-wrap gap-2" data-testid="service-type-filter">
        {SERVICE_TYPES.map(({ value, label }) => {
          const count = getTypeCount(value);
          const isActive = serviceTypeFilter === value;
          return (
            <button
              key={value}
              data-testid={`filter-type-${value}`}
              onClick={() => handleServiceTypeFilter(value)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all"
              style={{
                background: isActive ? 'var(--rv-teal)' : 'white',
                color: isActive ? 'white' : 'var(--rv-text-secondary)',
                border: isActive ? '1px solid var(--rv-teal)' : '1px solid var(--rv-border)',
                cursor: 'pointer',
                fontFamily: 'IBM Plex Sans, sans-serif',
              }}
            >
              {label}
              <span
                className="text-xs px-1.5 py-0.5 rounded-full"
                style={{
                  background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(13,148,136,0.08)',
                  color: isActive ? 'white' : 'var(--rv-teal)',
                  fontWeight: 600,
                  minWidth: '20px',
                  textAlign: 'center',
                }}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Подфильтр по подкатегориям "Общих услуг" */}
      {serviceTypeFilter === 'general' && (
        <div className="flex flex-wrap gap-2" data-testid="subcategory-filter">
          {[{ value: 'all', label: 'Все поручения' }, ...GENERAL_SUBCATEGORIES.map(s => ({ value: s.value, label: s.label }))].map(({ value, label }) => {
            const isActive = subcategoryFilter === value;
            return (
              <button
                key={value}
                data-testid={`filter-subcategory-${value}`}
                onClick={() => handleSubcategoryFilter(value)}
                className="px-3 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  background: isActive ? 'var(--rv-navy)' : 'white',
                  color: isActive ? 'white' : 'var(--rv-text-secondary)',
                  border: isActive ? '1px solid var(--rv-navy)' : '1px solid var(--rv-border)',
                  cursor: 'pointer',
                  fontFamily: 'IBM Plex Sans, sans-serif',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* Инструкция для исполнителей */}
      {user.role === "executor" && (
        <div className="rounded-xl overflow-hidden" style={{border:'1px solid #BFDBFE', background:'#EFF6FF'}}>
          <button
            data-testid="toggle-instructions-btn"
            onClick={() => {
              const next = !showInstructions;
              setShowInstructions(next);
              localStorage.setItem('reversia_instructions_open', next ? '1' : '0');
            }}
            className="w-full px-4 py-3 flex items-center justify-between text-left"
            style={{background:'none', border:'none', cursor:'pointer'}}
          >
            <span className="text-sm font-semibold" style={{color:'#1D4ED8'}}>Как взять задание в работу?</span>
            {showInstructions ? <ChevronUp size={14} style={{color:'#3B82F6'}} /> : <ChevronDown size={14} style={{color:'#3B82F6'}} />}
          </button>
          {showInstructions && (
            <div className="px-4 pb-4">
              {[
                'Нажмите "Ознакомиться" на карточке задания — задание закрепится за вами, перейдёт в статус "Обсуждается" и сразу откроется чат с заказчиком.',
                'Всё общение с заказчиком происходит в чате. Если вы закрыли окно чата, найти его можно во вкладке "Чат" в верхнем меню или по кнопке "Открыть чат" на карточке задания.',
                'В чате задайте вопросы, уточните детали и объём работы. Если считаете цену заниженной — напишите об этом заказчику: он может изменить стоимость задания, а вам останется подтвердить новую цену.',
                'Когда все условия согласованы — нажмите "Принять задание". Оно перейдёт в статус "В работе". Если условия не подошли, откажитесь от обсуждения — задание вернётся в общий список.',
                'Выполните задание и загрузите результаты (фото, видео, файлы) во вкладке "Мои задания". После подтверждения заказчиком вы получите оплату.',
                'Не видите подходящих заданий? Увеличьте зону поиска через параметр "Радиус поиска" над списком заданий — задания подбираются вокруг вашего местоположения в выбранном радиусе (от 10 до 250 км).',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-2.5 mb-2 text-sm" style={{color:'#374151'}}>
                  <span className="font-bold flex-shrink-0" style={{color:'#2563EB', minWidth:'18px'}}>{i+1}.</span>
                  <span dangerouslySetInnerHTML={{__html: text.replace(/"([^"]+)"/g, '<strong>"$1"</strong>')}} />
                </div>
              ))}
              <div className="mt-3 px-3 py-2.5 rounded-lg text-sm" style={{background:'#DBEAFE', color:'#1E40AF'}}>
                <strong>Важно:</strong> после нажатия "Ознакомиться" задание не берётся в работу автоматически — сначала обсудите детали с заказчиком в чате, и только потом принимайте задание.
              </div>
              <div className="mt-2 px-3 py-2.5 rounded-lg text-sm flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between" style={{background:'rgba(13,148,136,0.08)', color:'#0F766E'}} data-testid="notifications-hint-block">
                <span>
                  <strong>Совет:</strong> включите Push-уведомления, Гео-уведомления и Telegram-уведомления — вы узнаете о новых заданиях рядом с вами первыми и не пропустите сообщения заказчиков.
                </span>
                {setCurrentView && (
                  <button
                    data-testid="open-notification-settings-btn"
                    onClick={() => setCurrentView('notifications')}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium flex-shrink-0"
                    style={{background:'var(--rv-teal)', color:'white', border:'none', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
                  >
                    Настроить уведомления
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Map */}
      {showMap && (
        <div ref={mapSectionRef} data-testid="task-map-section" className="reversia-card p-0 overflow-hidden">
          <TaskMap tasks={mapTasks.filter(t => !isRemoteTask(t))} userLocation={userLocation} onMapReady={setMapInstance} radius={radius} />
          {!userLocation && (
            <p className="text-sm text-center py-2" style={{color:'var(--rv-text-muted)'}}>
              Разрешите доступ к геолокации, чтобы видеть своё положение на карте
            </p>
          )}
        </div>
      )}
      
      {searchQuery && (typeCounts.all === 0 || totalTasks === 0) ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3" data-testid="task-search-empty">
          <MapPin size={40} style={{color:'var(--rv-text-muted)', opacity:.5}} />
          <p className="text-base font-medium text-center" style={{color:'var(--rv-text-secondary)'}}>
            Ничего не найдено — измените запрос или радиус
          </p>
          <button
            data-testid="task-search-empty-reset"
            onClick={() => handleSearch('')}
            className="text-sm px-4 py-1.5 rounded-full"
            style={{border:'1px solid var(--rv-teal)', color:'var(--rv-teal)', background:'white', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
          >
            Сбросить поиск
          </button>
        </div>
      ) : typeCounts.all === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <MapPin size={40} style={{color:'var(--rv-text-muted)', opacity:.5}} />
          <p className="text-base font-medium" style={{color:'var(--rv-text-secondary)'}}>
            Нет доступных заданий
          </p>
          {!userLocation && (
            <p className="text-sm text-center max-w-xs" style={{color:'var(--rv-text-muted)'}}>
              Разрешите доступ к геолокации для поиска заданий рядом с вами
            </p>
          )}
        </div>
      ) : totalTasks === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <MapPin size={40} style={{color:'var(--rv-text-muted)', opacity:.5}} />
          <p className="text-base font-medium" style={{color:'var(--rv-text-secondary)'}}>
            Нет заданий данного типа
          </p>
          <button
            onClick={() => handleServiceTypeFilter('all')}
            className="text-sm px-4 py-1.5 rounded-full"
            style={{border:'1px solid var(--rv-teal)', color:'var(--rv-teal)', background:'white', cursor:'pointer', fontFamily:'IBM Plex Sans,sans-serif'}}
          >
            Показать все задания
          </button>
        </div>
      ) : (<>
        <div className="grid gap-4">
          {tasks.map((task) => (
            <div
              key={task.id}
              data-testid="task-card"
              className="bg-white rounded-xl p-4 sm:p-5 transition-all overflow-hidden w-full"
              style={{border:'1px solid var(--rv-border)', boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}
            >
              {/* Card header */}
              <div className="flex justify-between items-start gap-3 mb-3">
                <div className="flex-1 min-w-0 overflow-hidden">
                  <h3 className="font-semibold text-base mb-1.5 break-words leading-snug"
                    style={{fontFamily:'Manrope,sans-serif', color:'var(--rv-navy)'}}>
                    {task.title}
                  </h3>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {getServiceBadge(task)}
                    {(!task.service_type || task.service_type === "general") && task.task_subcategory && getSubcategoryLabel(task.task_subcategory) && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
                        data-testid="card-subcategory-badge"
                        style={{background:'#F1F5F9', color:'#334155'}}>
                        {getSubcategoryLabel(task.task_subcategory)}
                      </span>
                    )}
                    {isRemoteTask(task) && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
                        data-testid="card-remote-badge"
                        style={{background:'#EFF6FF', color:'#2563EB'}}>
                        Удалённо
                      </span>
                    )}
                    {task.is_b2b && <B2BTaskBadge />}
                    <EvidenceBadge task={task} testId={`card-evidence-badge-${task.id}`} />
                    {task.verified_executors_only && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
                        data-testid="card-verified-only-badge"
                        style={{background:'#EFF6FF', color:'#1E3A8A', border:'1px solid #93C5FD'}}>
                        Только для проверенных исполнителей
                      </span>
                    )}
                    {task.service_type === "mobile_journalist" && task.media_content_type && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{background:'var(--rv-teal-light)', color:'var(--rv-teal-dark)'}}>
                        {task.media_content_type === "photo" && "Фото"}
                        {task.media_content_type === "video" && "Видео"}
                        {task.media_content_type === "photo_video" && "Фото и видео"}
                      </span>
                    )}
                    {task.service_type === "mobile_journalist" && user.role === "executor" && !user.journalist_verified && (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium"
                        style={{background:'#FEF3C7', color:'#92400E'}}>
                        Аварийный режим
                      </span>
                    )}
                  </div>
                </div>
                {/* Price & deadline */}
                <div className="text-right flex-shrink-0">
                  <div className="text-lg font-bold" style={{color:'#059669', fontFamily:'Manrope,sans-serif'}}>
                    {task.price} ₽
                  </div>
                  <div className="flex items-center gap-1 justify-end mt-0.5">
                    <Clock size={11} style={{color:'var(--rv-text-muted)'}} />
                    <span className="text-xs" style={{color:'var(--rv-text-muted)'}}>
                      {new Date(task.deadline).toLocaleDateString('ru-RU', {day:'numeric',month:'short'})}
                    </span>
                  </div>
                </div>
              </div>

              {/* Location (TZ10-1: у удалённого задания блока адреса/расстояния/маршрута нет) */}
              {!isRemoteTask(task) && (
              <div className="flex items-center gap-3 flex-wrap mb-3" data-testid="card-location-block">
                <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
                  <MapPin size={13} style={{color:'var(--rv-text-muted)', flexShrink:0}} />
                  <span className="text-sm break-all" style={{color:'var(--rv-text-secondary)'}}>{task.location}</span>
                </div>
                {task.location_required !== false && task.latitude && task.longitude && (
                  <button
                    onClick={() => focusOnTaskLocation(task)}
                    className="text-xs flex items-center gap-1 hover:underline"
                    style={{color:'var(--rv-teal)', background:'none', border:'none', cursor:'pointer', padding:0}}
                  >
                    <Map size={11} />на карте
                  </button>
                )}
                {task.location_required !== false && task.distance != null && (
                  <span className="text-xs font-medium" style={{color:'#2563EB'}}>
                    {task.distance} км {getEstimatedTime(task.distance) && `(${getEstimatedTime(task.distance)})`}
                  </span>
                )}
                {task.location_required !== false && task.latitude && task.longitude && (
                  <button
                    onClick={() => openNavigator(task)}
                    className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium"
                    style={{background:'#EFF6FF', color:'#2563EB', border:'none', cursor:'pointer'}}
                  >
                    <Navigation size={11} />Маршрут
                  </button>
                )}
              </div>
              )}

              <TaskDescription text={task.description} taskId={task.id} />
              
              {/* Actions */}
              {user.role === "executor" && (task.status === "pending" || task.status === "open") && (
                task.priority_locked ? (
                  <PriorityTimer
                    initialSeconds={task.priority_available_in_seconds || 0}
                    onExpired={() => setTasks(prev => prev.map(t =>
                      t.id === task.id ? { ...t, priority_locked: false } : t
                    ))}
                  />
                ) : task.is_b2b && !hasBusinessStatus(user) ? (
                  <div className="space-y-1.5">
                    <button
                      data-testid="task-familiarize-btn-disabled"
                      disabled
                      className="reversia-btn reversia-btn-md"
                      style={{background:'#E2E8F0', color:'#64748B', cursor:'not-allowed'}}
                      title="Задание от организации: нужен статус самозанятого/ИП в профиле"
                    >
                      Ознакомиться
                    </button>
                    <p className="text-xs" data-testid="b2b-status-required-hint" style={{color:'#0C4A6E'}}>
                      Задание от организации: нужен статус самозанятого/ИП в профиле (настройки → бизнес-статус).
                      {setCurrentView && (
                        <button type="button" data-testid="b2b-go-to-status-btn" onClick={() => setCurrentView('business-status')} className="ml-1 underline font-medium">
                          Указать статус
                        </button>
                      )}
                    </p>
                  </div>
                ) : (
                  <button
                    data-testid="task-familiarize-btn"
                    onClick={() => startTaskDiscussion(task.id)}
                    className="reversia-btn reversia-btn-md"
                    style={{background:'var(--rv-teal)', color:'white'}}
                  >
                    Ознакомиться
                  </button>
                )
              )}
              
              {user.role === "executor" && task.status === "discussing" && task.discussing_executor_id === user.id && (
                <div className="space-y-2.5">
                  <p className="text-sm" style={{color:'var(--rv-text-secondary)'}}>Задание обсуждается с заказчиком. Используйте чат для уточнения деталей.</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => setSelectedTask(task)}
                      className="reversia-btn reversia-btn-teal reversia-btn-md flex items-center gap-1.5"
                    >
                      <MessageSquare size={14} />Открыть чат
                    </button>
                    <button
                      onClick={() => acceptTask(task.id)}
                      className="reversia-btn reversia-btn-md flex items-center gap-1.5"
                      style={{background:'#059669', color:'white'}}
                    >
                      <Check size={14} />Принять задание
                    </button>
                  </div>
                </div>
              )}
              
              {user.role === "executor" && task.status === "discussing" && task.discussing_executor_id !== user.id && (
                <p className="text-sm" style={{color:'var(--rv-text-muted)'}}>Обсуждается с другим исполнителем</p>
              )}
            </div>
          ))}
        </div>
        <PaginationControls
          total={totalTasks}
          perPage={perPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          onPerPageChange={setPerPage}
        />
      </>)}

      {/* TZ8-6: единая чат-модаль (эталон раздела «Чат») */}
      {selectedTask && (
        <TaskChatModal
          task={selectedTask}
          user={user}
          showToast={showToast}
          testId="available-task-chat-modal"
          onClose={() => setSelectedTask(null)}
          onChanged={fetchTasks}
          onAccepted={() => { if (typeof window !== 'undefined' && window.ym) window.ym(109617150, 'reachGoal', 'task_accepted'); }}
        />
      )}
    </div>
  );
};
